import { useState, useEffect } from 'react';
import { ncService, buildExportParams } from '../services/api';
import authService from '../services/authService';
import API_URL from '../config';
import NCListSkeleton from './NCListSkeleton';
import { showSuccess, showError } from '../utils/toast';
import { cssSlug } from '../utils/cssSlug';
import { getSeveridadeLabel, getSeveridadeCor } from '../utils/severidade';
import { formatDate as formatDateTenant } from '../utils/formatDateTime';
import PageHeader from './ui/PageHeader';
import Button from './ui/Button';

import FilterChip from './ui/FilterChip';
import StateBlock from './ui/StateBlock';
import NCImportCSV from './NCImportCSV';
import './NCList.css';

const SERVICE_SECTOR_VALUES = new Set([
  'contabil', 'saude', 'logistica', 'facilities', 'laboratorios', 'ti',
  'engenharia_projetos', 'servicos',
  'contábil / fiscal', 'saúde / clínicas', 'logística / armazenagem (3pl)',
  'facilities / terceirizados', 'laboratórios (calibração/ensaios)', 'ti / software',
  'engenharia / projetos', 'serviços',
]);
const isServiceSector = (industria) =>
  industria != null && SERVICE_SECTOR_VALUES.has(String(industria).toLowerCase().trim());

const PER_PAGE = 20; // #78a: itens por página (fixo no MVP, sem seletor)
const EXPORT_LIMITE = 5000; // #78b: teto de NCs por export (espelha o backend)

// #160: lembrar o filtro da lista (por DISPOSITIVO, localStorage). Só os campos
// que o estado de filtro lê do seed entram aqui — mesmo shape do initialFilters
// do drill (sem `busca`, que é input volátil, e sem paginação).
const PREF_FILTROS_KEY = 'qaasa_nclist_filtros';
const _lerFiltrosSalvos = () => {
  try {
    const raw = localStorage.getItem(PREF_FILTROS_KEY);
    const obj = raw ? JSON.parse(raw) : null;
    return obj && typeof obj === 'object' ? obj : null;
  } catch (e) { return null; }
};
const _temFiltro = (f) => !!f && typeof f === 'object' && Object.keys(f).length > 0;

function NCList({ clienteId, onSelectNC, user, initialFilters }) {
  // #52 (Sessão 33): drill-down do painel. initialFilters semeia o estado dos
  // filtros no mount (NCList remonta a cada drill, então a leitura é sempre fresca).
  // #160: precedência do seed — (1) drill do painel (initialFilters não-vazio)
  // VENCE e passa a ser o salvo; (2) senão, filtro lembrado do localStorage;
  // (3) senão, vazio (defaults atuais). Ausência da chave = comportamento de hoje.
  const _drill = _temFiltro(initialFilters) ? initialFilters : null;
  const _if = _drill || _lerFiltrosSalvos() || {};
  const [ncs, setNcs] = useState([]);
  const [total, setTotal] = useState(0);          // #78a: total server-side (reflete filtros)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // #78a: searchTerm = input imediato (controla o campo + reseta página);
  // debouncedSearch = valor que dispara o fetch (300ms depois).
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState(_if.status || 'todos');
  const [filterSeveridade, setFilterSeveridade] = useState(_if.severidade || 'todos');
  const [filterOrigem, setFilterOrigem] = useState(_if.origem_tipo || 'todos');       // #78a
  const [filterFornecedor, setFilterFornecedor] = useState(_if.fornecedor_id != null ? String(_if.fornecedor_id) : 'todos'); // #78a (id ou 'todos')
  const [filterClienteEmissor, setFilterClienteEmissor] = useState(_if.cliente_emissor || 'todos'); // #105 (nome ou 'todos')
  const [filterEficacia, setFilterEficacia] = useState(_if.eficacia || 'todos');   // #78a
  const [dataInicio, setDataInicio] = useState(_if.data_inicio || '');             // #78a (YYYY-MM-DD)
  const [dataFim, setDataFim] = useState(_if.data_fim || '');                      // #78a
  // #52: filtros bool do drill-down do painel (sem chip próprio — banner clearable).
  const [filterDemandaAcao, setFilterDemandaAcao] = useState(!!_if.demanda_acao);
  const [filterEmAtraso, setFilterEmAtraso] = useState(!!_if.em_atraso);
  const [filterCriticas, setFilterCriticas] = useState(!!_if.criticas);
  const [filterTemCusto, setFilterTemCusto] = useState(!!_if.tem_custo);  // #52-financeiro drill
  const [filterCategoriaImpacto, setFilterCategoriaImpacto] = useState(_if.categoria_impacto || 'todos');  // #108
  const [filterSort, setFilterSort] = useState(_if.sort || 'recentes');   // #137: ordenação (recentes|custo_desc|custo_asc)
  const [currentPage, setCurrentPage] = useState(1);               // #78a: paginação real
  const [fornecedores, setFornecedores] = useState([]);            // #78a: dropdown de fornecedor
  const [clientesEmissores, setClientesEmissores] = useState([]);  // #105: dropdown de cliente emissor
  const [viewMode, setViewMode] = useState('grid');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [exportando, setExportando] = useState(false); // #78b
  const [showImportCsv, setShowImportCsv] = useState(false); // #179
  const [carregandoExemplo, setCarregandoExemplo] = useState(false); // #238

  // Para auditor
  const [clientes, setClientes] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(clienteId);
  const [clienteInfo, setClienteInfo] = useState(null);

  const isAuditor = user && user.role === 'auditor';

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  // #78a: reset de página + setter de filtro num helper (reset SÍNCRONO no onChange).
  const aplicarFiltro = (setter) => (valor) => {
    setter(valor);
    setCurrentPage(1);
  };

  // #78a: janela compacta de páginas (com "…") quando há muitas páginas.
  const paginas = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const alvo = new Set([1, 2, totalPages - 1, totalPages, currentPage - 1, currentPage, currentPage + 1]);
    const nums = [...alvo].filter((n) => n >= 1 && n <= totalPages).sort((a, b) => a - b);
    const out = [];
    let prev = 0;
    for (const n of nums) {
      if (n - prev > 1) out.push('…');
      out.push(n);
      prev = n;
    }
    return out;
  };

  // Buscar lista de clientes (apenas para auditor)
  useEffect(() => {
    if (isAuditor) {
      fetchClientes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuditor]);

  // #78a: debounce da busca (300ms). Só o FETCH é adiado — o reset de página
  // acontece no onChange (síncrono), então não há "pula pra página 1 depois".
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // #78a/#105: fornecedores + clientes emissores do tenant pros dropdowns de filtro
  // (recarrega ao trocar cliente)
  useEffect(() => {
    if (selectedClientId) {
      fetchFornecedores();
      fetchClientesEmissores();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId]);

  // #78a: fetch server-side — dispara em qualquer mudança de filtro/página/busca-debounced
  useEffect(() => {
    if (selectedClientId) {
      fetchNCs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId, filterStatus, filterSeveridade, filterOrigem, filterFornecedor,
      filterClienteEmissor, filterDemandaAcao, filterEmAtraso, filterCriticas, filterTemCusto,
      filterCategoriaImpacto, filterSort, filterEficacia, dataInicio, dataFim, debouncedSearch, currentPage]);

  const fetchClientes = async () => {
    try {
      const data = await ncService.listClientes();
      setClientes(data.clientes || []);
    } catch (error) {
      console.error('Erro ao buscar clientes:', error);
      setError('Erro ao carregar lista de clientes');
    }
  };

  const fetchFornecedores = async () => {
    try {
      const data = await ncService.listFornecedores(selectedClientId);
      setFornecedores(data.fornecedores || []);
    } catch (error) {
      // não crítico: sem fornecedores, o filtro simplesmente não aparece
      setFornecedores([]);
    }
  };

  // #105: clientes emissores distintos do tenant pro dropdown de filtro
  const fetchClientesEmissores = async () => {
    try {
      const data = await ncService.listClientesEmissores(selectedClientId);
      setClientesEmissores(data.clientes_emissores || []);
    } catch (error) {
      // não crítico: sem emissores, o filtro simplesmente não aparece
      setClientesEmissores([]);
    }
  };

  const fetchNCs = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await ncService.listNCs(selectedClientId, {
        skip: (currentPage - 1) * PER_PAGE,
        limit: PER_PAGE,
        status: filterStatus,
        severidade: filterSeveridade,
        origem_tipo: filterOrigem,
        fornecedor_id: filterFornecedor !== 'todos' ? filterFornecedor : undefined,
        cliente_emissor: filterClienteEmissor !== 'todos' ? filterClienteEmissor : undefined,
        demanda_acao: filterDemandaAcao || undefined,  // #52 drill-down
        em_atraso: filterEmAtraso || undefined,        // #52 drill-down
        criticas: filterCriticas || undefined,         // #52 drill-down
        tem_custo: filterTemCusto || undefined,        // #52-financeiro drill
        categoria_impacto: filterCategoriaImpacto !== 'todos' ? filterCategoriaImpacto : undefined,  // #108
        sort: filterSort !== 'recentes' ? filterSort : undefined,  // #137: ordenação por custo
        eficacia: filterEficacia,
        data_inicio: dataInicio || undefined,
        data_fim: dataFim || undefined,
        busca: debouncedSearch || undefined,
      });
      setNcs(data.ncs || []);
      setTotal(data.total || 0);
      setClienteInfo(data.cliente);
    } catch (error) {
      console.error('Erro ao buscar NCs:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Erro ao carregar não conformidades';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const isAdmin = user?.role === 'admin';
  // #78b: export é operação operacional — admin + operador (auditor não).
  const podeExportar = user?.role === 'admin' || user?.role === 'operador';
  const exportBloqueadoPorTeto = total > EXPORT_LIMITE;

  // #238: conta genuinamente vazia (sem NENHUM filtro ativo) — só aí o
  // empty-state oferece a NC de exemplo (em vez de "filtrado sem match").
  const semFiltrosAtivos = (
    !debouncedSearch &&
    filterStatus === 'todos' && filterSeveridade === 'todos' &&
    filterOrigem === 'todos' && filterFornecedor === 'todos' &&
    filterClienteEmissor === 'todos' && filterEficacia === 'todos' &&
    filterCategoriaImpacto === 'todos' && !dataInicio && !dataFim &&
    !filterDemandaAcao && !filterEmAtraso && !filterCriticas && !filterTemCusto
  );

  // #238: carrega a NC de exemplo (aditivo) e abre o detalhe. Idempotente no back.
  const handleCarregarExemplo = async () => {
    if (carregandoExemplo) return;
    setCarregandoExemplo(true);
    try {
      const r = await ncService.carregarExemplo();
      showSuccess(r.ja_existia
        ? 'Abrindo a NC de exemplo.'
        : 'NC de exemplo criada! Explore a análise completa e o relatório.');
      onSelectNC(r.nc_id);
    } catch (e) {
      showError('Não foi possível carregar o exemplo. Tente novamente.');
    } finally {
      setCarregandoExemplo(false);
    }
  };

  // Filtros atuais da tela (mesma semântica do fetch da lista).
  const filtrosAtuais = () => ({
    status: filterStatus,
    severidade: filterSeveridade,
    origem_tipo: filterOrigem,
    fornecedor_id: filterFornecedor,
    cliente_emissor: filterClienteEmissor,
    demanda_acao: filterDemandaAcao || undefined,  // #52 drill-down
    em_atraso: filterEmAtraso || undefined,        // #52 drill-down
    criticas: filterCriticas || undefined,         // #52 drill-down
    tem_custo: filterTemCusto || undefined,        // #52-financeiro drill
    categoria_impacto: filterCategoriaImpacto !== 'todos' ? filterCategoriaImpacto : undefined,  // #108
    data_inicio: dataInicio,
    data_fim: dataFim,
    eficacia: filterEficacia,
    busca: debouncedSearch,
  });

  // #160: snapshot do filtro pra persistir — MESMO shape que o seed (_if) lê no
  // mount (sem `busca`/paginação). É o que será restaurado no próximo mount.
  const _snapshotFiltros = () => {
    const s = {};
    if (filterStatus !== 'todos') s.status = filterStatus;
    if (filterSeveridade !== 'todos') s.severidade = filterSeveridade;
    if (filterOrigem !== 'todos') s.origem_tipo = filterOrigem;
    if (filterFornecedor !== 'todos') s.fornecedor_id = filterFornecedor;
    if (filterClienteEmissor !== 'todos') s.cliente_emissor = filterClienteEmissor;
    if (filterEficacia !== 'todos') s.eficacia = filterEficacia;
    if (filterCategoriaImpacto !== 'todos') s.categoria_impacto = filterCategoriaImpacto;
    if (filterSort !== 'recentes') s.sort = filterSort;
    if (dataInicio) s.data_inicio = dataInicio;
    if (dataFim) s.data_fim = dataFim;
    if (filterDemandaAcao) s.demanda_acao = true;
    if (filterEmAtraso) s.em_atraso = true;
    if (filterCriticas) s.criticas = true;
    if (filterTemCusto) s.tem_custo = true;
    return s;
  };

  // #160: persiste o filtro atual no localStorage (por dispositivo) a cada
  // mudança. Vazio = remove a chave (volta ao default no próximo mount). Como o
  // seed do drill já entrou no estado no mount, ESTE efeito também grava o drill
  // → o filtro do painel "vence" e passa a ser o lembrado (spec #160). `busca`
  // fica de fora de propósito (input volátil, não-lembrado).
  useEffect(() => {
    try {
      const snap = _snapshotFiltros();
      if (Object.keys(snap).length > 0) {
        localStorage.setItem(PREF_FILTROS_KEY, JSON.stringify(snap));
      } else {
        localStorage.removeItem(PREF_FILTROS_KEY);
      }
    } catch (e) { /* localStorage indisponível — ignora */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, filterSeveridade, filterOrigem, filterFornecedor, filterClienteEmissor,
      filterEficacia, filterCategoriaImpacto, filterSort, dataInicio, dataFim,
      filterDemandaAcao, filterEmAtraso, filterCriticas, filterTemCusto]);

  // #52: rótulo do filtro de painel ativo (banner clearable) + reset total.
  const drillLabel = filterEmAtraso ? 'Em atraso'
    : filterCriticas ? 'Críticas (pendentes)'
    : filterDemandaAcao ? 'Pendentes (demandam ação)'
    : filterTemCusto ? 'Com custo informado'
    : filterCategoriaImpacto !== 'todos'  // #108
      ? `Impacto: ${({ financeiro: 'Financeiro', reputacional: 'Reputacional', operacional: 'Operacional' }[filterCategoriaImpacto] || filterCategoriaImpacto)}`
    : null;
  const limparFiltrosPainel = () => {
    setFilterDemandaAcao(false);
    setFilterEmAtraso(false);
    setFilterCriticas(false);
    setFilterTemCusto(false);
    setFilterCategoriaImpacto('todos');  // #108
    setFilterStatus('todos');
    setFilterSeveridade('todos');
    setFilterOrigem('todos');
    setFilterFornecedor('todos');
    setFilterClienteEmissor('todos');
    setFilterEficacia('todos');
    setFilterSort('recentes');  // #137: volta pro default de ordenação
    setDataInicio('');
    setDataFim('');
    setCurrentPage(1);
    // #160: limpar filtros também ZERA o filtro lembrado (o effect de persistência
    // já removeria com o snapshot vazio; removemos aqui explícito por robustez).
    try { localStorage.removeItem(PREF_FILTROS_KEY); } catch (e) { /* ignora */ }
  };

  const handleExportCsv = async () => {
    if (exportando) return;
    setExportando(true);
    try {
      const blob = await ncService.exportarNCsCsv(selectedClientId, filtrosAtuais());
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `qaasa-ncs-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const st = err.response?.status;
      if (st === 403) showError('Apenas administradores e operadores podem exportar.');
      else if (st === 400) showError(`Refine os filtros — export limitado a ${EXPORT_LIMITE} NCs.`);
      else showError(`Erro ao exportar: ${err.response?.data?.detail || err.message}`);
    } finally {
      setExportando(false);
    }
  };

  const handleExportPdf = () => {
    const params = new URLSearchParams({
      cliente_id: String(selectedClientId),
      ...buildExportParams(filtrosAtuais()),
    });
    window.open(`/exportar-ncs?${params.toString()}`, '_blank');
  };

  const toggleSelect = (e, ncId) => {
    e.stopPropagation();
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(ncId)) next.delete(ncId);
      else next.add(ncId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    // #78a: opera sobre a página atual (ncs já vem paginado do servidor)
    if (selectedIds.size === ncs.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(ncs.map(n => n.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    const count = selectedIds.size;
    if (!window.confirm(`Excluir ${count} NC${count > 1 ? 's' : ''}? Esta ação não pode ser desfeita.`)) return;

    setDeleting(true);
    const token = authService.getToken();
    const ids = Array.from(selectedIds);
    let successCount = 0;
    const failures = [];

    for (const id of ids) {
      try {
        const res = await fetch(`${API_URL}/api/v3/nc/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (res.ok) {
          successCount += 1;
        } else {
          const data = await res.json().catch(() => ({}));
          failures.push(`NC-${id}: ${data.detail || res.status}`);
        }
      } catch (err) {
        failures.push(`NC-${id}: ${err.message}`);
      }
    }

    setDeleting(false);
    setSelectedIds(new Set());

    if (successCount > 0) {
      showSuccess(`${successCount} NC${successCount > 1 ? 's excluídas' : ' excluída'} com sucesso.`);
    }
    if (failures.length > 0) {
      showError(`Falha ao excluir ${failures.length}: ${failures.slice(0, 3).join('; ')}`);
    }
    fetchNCs();
  };

  // #78a: filtragem + paginação agora são SERVER-SIDE (vide fetchNCs). `ncs` já vem
  // filtrado e paginado do backend — nada de filtro client-side aqui (senão mentiria
  // sobre o total, que reflete os filtros no servidor).

  // #78a: badge de eficácia na linha — filtro=fila (binário), badge=resultado (visual).
  // Ineficaz grita em vermelho; eficaz/parcial discretos; pendente/sem avaliação não mostra.
  const getEficaciaBadge = (nc) => {
    const ef = nc.eficacia_avaliada;
    if (!ef || ef === 'pendente') return null;
    const cfg = {
      eficaz: { label: 'Eficaz', cls: 'nc-eficacia-eficaz' },
      parcialmente_eficaz: { label: 'Parcial', cls: 'nc-eficacia-parcial' },
      ineficaz: { label: 'Ineficaz', cls: 'nc-eficacia-ineficaz' },
    }[ef];
    if (!cfg) return null;
    return <span className={`nc-eficacia-badge ${cfg.cls}`}>{cfg.label}</span>;
  };

  const getSeverityBadge = (severidade) => {
    // TD86: usar cssSlug pra remover acentos. Antes este código tinha
    // chaves duplicadas no map `icons` (`crítica`/`critica`, `média`/`media`)
    // como workaround porque `.toLowerCase()` mantém acento. Com cssSlug
    // normalizando pra ascii, basta uma chave por severidade. A classe CSS
    // da linha do badge agora sempre casa com `.nc-severity-badge.alta/.media/...`.
    const s = cssSlug(severidade) || 'media';
    const icons = {
      critica: { icon: 'warning', color: 'var(--error)' },
      alta:    { icon: 'warning', color: 'var(--error)' },
      media:   { icon: 'warning', color: 'var(--amber-500)' },
      baixa:   { icon: 'info', color: 'var(--blue-500)' },
    };
    const cfg = icons[s] || icons['media'];
    // #67: rótulo + cor custom do tenant (null = mantém o token de tema default).
    const rotulo = getSeveridadeLabel(severidade);
    const corCustom = getSeveridadeCor(severidade);
    return (
      <div className="nc-severity-group">
        <span className="material-symbols-outlined nc-severity-icon" style={{ color: corCustom || cfg.color }}>{cfg.icon}</span>
        <span className={`nc-severity-badge ${s}`} style={corCustom ? { color: corCustom } : undefined}>{rotulo}</span>
      </div>
    );
  };

  const getStatusBadge = (status) => {
    const badges = {
      // v3.4.0 — status oficiais
      rascunho:             { label: 'Rascunho',             cls: 'nc-status-rascunho' },
      aberta:               { label: 'Aberta',               cls: 'nc-status-aberta' },
      em_analise:           { label: 'Em Análise',           cls: 'nc-status-em_analise' },
      aguardando_aprovacao: { label: 'Aguard. Aprovação',    cls: 'nc-status-aguardando' },
      aprovada:             { label: 'Aprovada',             cls: 'nc-status-aprovada' },
      encerrada:            { label: 'Encerrada',            cls: 'nc-status-encerrada' },
      rejeitada:            { label: 'Rejeitada',            cls: 'nc-status-rejeitada' },
      // legado v2.0 — mantido para NCs antigas
      em_revisao:           { label: 'Em Revisão',           cls: 'nc-status-em_analise' },
      aprovado:             { label: 'Aprovado',             cls: 'nc-status-aprovada' },
      reprovado:            { label: 'Reprovado',            cls: 'nc-status-rejeitada' },
    };
    const badge = badges[status] || { label: status || 'N/A', cls: 'nc-status-default' };
    return (
      <span className={`nc-card-status ${badge.cls}`}>{badge.label}</span>
    );
  };

  // #78 (Sessão 31): badge de origem da NC + nome do fornecedor (modo outbound).
  // Apenas visual — NÃO há filtro client-side em cima disso (filtro fica pro #78a
  // server-side, senão a paginação truncada de 50 mentiria sobre o resultado).
  // Vocabulário canônico (alinhar com formulário/relatório/dashboard na #78a):
  //   fornecedor → "Outbound" | cliente → "Cliente externo" | proprio → "Própria".
  const getOrigemBadge = (nc) => {
    const origem = nc.origem_tipo || 'proprio';
    const cfg = {
      fornecedor: { label: 'Outbound',        cls: 'nc-origem-fornecedor' },
      cliente:    { label: 'Cliente externo', cls: 'nc-origem-cliente' },
      proprio:    { label: 'Própria',          cls: 'nc-origem-proprio' },
    }[origem] || { label: 'Própria', cls: 'nc-origem-proprio' };
    // #105: sufixo com a outra parte envolvida — fornecedor (outbound) ou
    // cliente emissor (origem cliente). Simétrico, só visual.
    let sufixo = '';
    if (origem === 'fornecedor' && nc.fornecedor_nome) sufixo = ` · ${nc.fornecedor_nome}`;
    else if (origem === 'cliente' && nc.cliente_emissor) sufixo = ` · ${nc.cliente_emissor}`;
    return (
      <span className={`nc-origem-badge ${cfg.cls}`}>
        {cfg.label}{sufixo}
      </span>
    );
  };

  // Decisão 2 Sessão 22: usa timezone do tenant via formatDateTenant.
  // `toLocaleDateString` antigo usava timezone do BROWSER — usuários
  // em fusos diferentes do tenant viam datas inconsistentes. Agora a
  // data renderiza sempre no fuso da empresa logada (user.client_timezone).
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return formatDateTenant(dateStr, user);
  };

  // #78a: skeleton SÓ no primeiro carregamento (lista vazia). Em refetch de
  // filtro/página, a lista atual permanece com dim sutil (vide nc-list--refetching).
  if (loading && ncs.length === 0) {
    return (
      <div className="nc-list-container nc-list-loading">
        <PageHeader kicker="Gestão de Qualidade" title="Não Conformidades" />
        <NCListSkeleton count={5} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="nc-list-container">
        <PageHeader kicker="Gestão de Qualidade" title="Não Conformidades" />
        <StateBlock
          kind="error"
          title="Erro ao carregar"
          message={`${error} — verifique sua conexão ou tente fazer login novamente.`}
        />
      </div>
    );
  }

  return (
    <div className={`nc-list-container${loading ? ' nc-list--refetching' : ''}`}>
      {/* #78a: microindicador de refetch (dim na lista vem via classe acima) */}
      {loading && <span className="nc-refetch-indicator" aria-hidden="true" />}
      {/* Header */}
      <PageHeader
        kicker="Gestão de Qualidade"
        title="Não Conformidades"
        subtitle={clienteInfo ? `Cliente: ${clienteInfo.nome}` : undefined}
        actions={
          <div className="nc-list-auditor">
            <span className="nc-list-auditor-label">{user?.role === 'auditor' ? 'Auditor' : user?.role === 'admin' ? 'Admin' : 'Operador'}</span>
            <span className="nc-list-auditor-name">{user?.nome}</span>
          </div>
        }
      />

      {/* #52: banner do drill-down do painel — explica o filtro invisível (bool) e permite limpar */}
      {drillLabel && (
        <div className="nc-drill-banner">
          <span>Filtrado pelo painel: <strong>{drillLabel}</strong></span>
          <button type="button" className="nc-drill-banner-clear" onClick={limparFiltrosPainel}>
            Limpar
          </button>
        </div>
      )}

      {/* Filtro de Cliente (apenas para auditor) */}
      {isAuditor && clientes.length > 0 && (
        <section className="nc-client-filter">
          <label htmlFor="client-select" className="nc-client-filter-label">
            Filtrar por Cliente:
          </label>
          <select
            id="client-select"
            className="nc-client-select"
            value={selectedClientId}
            onChange={(e) => setSelectedClientId(Number(e.target.value))}
          >
            {clientes.map((cliente) => (
              <option key={cliente.id} value={cliente.id}>
                {cliente.nome} {cliente.cnpj ? `(${cliente.cnpj})` : ''}
              </option>
            ))}
          </select>
          <span className="nc-client-count">
            {total} NC{total !== 1 ? 's' : ''}
          </span>
        </section>
      )}

      {/* Filters */}
      <section className="nc-list-filters">
        {/* Search — #78a: reset de página síncrono; fetch é debounced */}
        <div className="nc-search-wrapper">
          <span className="material-symbols-outlined nc-search-icon">search</span>
          <input
            type="text"
            className="nc-search-input"
            placeholder="Buscar por número, produto ou lote..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
          />
        </div>

        {/* Status chip */}
        <FilterChip
          icon="flag"
          value={filterStatus}
          onChange={aplicarFiltro(setFilterStatus)}
          placeholder="Status"
          options={[
            { value: 'rascunho', label: 'Rascunho' },
            { value: 'aberta', label: 'Aberta' },
            { value: 'em_analise', label: 'Em Análise' },
            { value: 'aguardando_aprovacao', label: 'Aguard. Aprovação' },
            { value: 'aprovada', label: 'Aprovada' },
            { value: 'encerrada', label: 'Encerrada' },
            { value: 'rejeitada', label: 'Rejeitada' },
          ]}
        />

        {/* Severidade chip */}
        <FilterChip
          icon="warning"
          value={filterSeveridade}
          onChange={aplicarFiltro(setFilterSeveridade)}
          placeholder="Severidade"
          options={[
            { value: 'baixa', label: 'Baixa' },
            { value: 'media', label: 'Média' },
            { value: 'alta', label: 'Alta' },
            { value: 'critica', label: 'Crítica' },
          ]}
        />

        {/* #78a: Origem chip */}
        <FilterChip
          icon="category"
          value={filterOrigem}
          onChange={aplicarFiltro(setFilterOrigem)}
          placeholder="Origem"
          options={[
            { value: 'proprio', label: 'Própria' },
            { value: 'cliente', label: 'Cliente externo' },
            { value: 'fornecedor', label: 'Outbound' },
          ]}
        />

        {/* #78a: Fornecedor chip — só aparece se o tenant tem fornecedores */}
        {fornecedores.length > 0 && (
          <FilterChip
            icon="local_shipping"
            value={filterFornecedor}
            onChange={aplicarFiltro(setFilterFornecedor)}
            placeholder="Fornecedor"
            options={fornecedores.map((f) => ({ value: String(f.id), label: f.nome }))}
          />
        )}

        {/* #105: Cliente emissor chip — só aparece se o tenant tem NCs de origem cliente */}
        {clientesEmissores.length > 0 && (
          <FilterChip
            icon="business"
            value={filterClienteEmissor}
            onChange={aplicarFiltro(setFilterClienteEmissor)}
            placeholder="Cliente emissor"
            options={clientesEmissores.map((c) => ({ value: c.nome, label: c.nome }))}
          />
        )}

        {/* #78a: Eficácia chip (bi-state: fila de verificação) */}
        <FilterChip
          icon="verified"
          value={filterEficacia}
          onChange={aplicarFiltro(setFilterEficacia)}
          placeholder="Eficácia"
          options={[
            { value: 'a_avaliar', label: 'A avaliar' },          // #176: drill do painel (data prevista + pendente + não-terminal)
            { value: 'sem_verificacao', label: 'Sem verificação' },
            { value: 'verificada', label: 'Verificada' },
          ]}
        />

        {/* #108: Categoria de impacto chip (drill do painel + filtro direto) */}
        <FilterChip
          icon="category"
          value={filterCategoriaImpacto}
          onChange={aplicarFiltro(setFilterCategoriaImpacto)}
          placeholder="Impacto"
          options={[
            { value: 'financeiro', label: 'Financeiro' },
            { value: 'reputacional', label: 'Reputacional' },
            { value: 'operacional', label: 'Operacional' },
          ]}
        />

        {/* #137: Ordenação — sempre tem valor ('recentes' é o default; sem opção
            "todos"). Fica ativo (verde) quando ordena por custo. */}
        <FilterChip
          icon="sort"
          value={filterSort}
          onChange={aplicarFiltro(setFilterSort)}
          active={filterSort !== 'recentes'}
          options={[
            { value: 'recentes', label: 'Mais recentes' },
            { value: 'custo_desc', label: 'Custo: maior primeiro' },
            { value: 'custo_asc', label: 'Custo: menor primeiro' },
          ]}
        />

        {/* #78a: Período (range de data de criação) */}
        <div className="nc-filter-datas">
          <input
            type="date"
            className="nc-filter-data"
            value={dataInicio}
            onChange={(e) => aplicarFiltro(setDataInicio)(e.target.value)}
            title="Data inicial"
          />
          <span className="nc-filter-data-sep">–</span>
          <input
            type="date"
            className="nc-filter-data"
            value={dataFim}
            onChange={(e) => aplicarFiltro(setDataFim)(e.target.value)}
            title="Data final"
          />
        </div>

        {/* View toggle (visual only) */}
        <div className="nc-view-toggle">
          <button
            className={`nc-view-btn ${viewMode === 'grid' ? 'active' : ''}`}
            onClick={() => setViewMode('grid')}
            title="Visualização em grade"
          >
            <span className="material-symbols-outlined" aria-hidden="true">grid_view</span>
          </button>
          <button
            className={`nc-view-btn ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')}
            title="Visualização em lista"
          >
            <span className="material-symbols-outlined" aria-hidden="true">view_list</span>
          </button>
        </div>

        {/* #78b: Exportar (admin/operador) — exporta o conjunto FILTRADO inteiro.
            Bloqueado acima do teto; abaixo, dropdown CSV/PDF. */}
        {podeExportar && (
          exportBloqueadoPorTeto ? (
            <button
              type="button"
              className="nc-export-btn"
              disabled
              title={`Refine os filtros — export limitado a ${EXPORT_LIMITE} NCs (atual: ${total})`}
            >
              <span className="material-symbols-outlined" aria-hidden="true">download</span>
              Exportar
            </button>
          ) : (
            <details className="nc-export">
              <summary className="nc-export-btn">
                <span className="material-symbols-outlined" aria-hidden="true">download</span>
                {exportando ? 'Exportando…' : 'Exportar'}
              </summary>
              <div className="nc-export-menu">
                <button type="button" onClick={handleExportCsv} disabled={exportando}>
                  CSV (dados)
                </button>
                <button type="button" onClick={handleExportPdf}>
                  PDF (documento)
                </button>
              </div>
            </details>
          )
        )}

        {/* #179: importar planilha (admin/operador, mesmo gate do export) */}
        {podeExportar && (
          <button
            type="button"
            className="nc-export-btn"
            onClick={() => setShowImportCsv(true)}
            title="Importar NCs de uma planilha (.csv)"
          >
            <span className="material-symbols-outlined" aria-hidden="true">upload_file</span>
            Importar planilha
          </button>
        )}
      </section>

      {showImportCsv && (
        <NCImportCSV
          onClose={() => setShowImportCsv(false)}
          onDone={() => { setShowImportCsv(false); fetchNCs(); }}
        />
      )}

      {/* Toolbar de seleção (admin) */}
      {isAdmin && ncs.length > 0 && (
        <section className="nc-list-toolbar">
          <label className="nc-select-all">
            <input
              type="checkbox"
              checked={selectedIds.size === ncs.length && ncs.length > 0}
              onChange={toggleSelectAll}
              disabled={deleting}
            />
            <span>{selectedIds.size > 0 ? `${selectedIds.size} selecionada${selectedIds.size > 1 ? 's' : ''}` : 'Selecionar todas'}</span>
          </label>
          {selectedIds.size > 0 && (
            <Button
              variant="danger"
              size="sm"
              icon="delete"
              onClick={handleBulkDelete}
              disabled={deleting}
            >
              {deleting ? 'Excluindo...' : `Excluir ${selectedIds.size}`}
            </Button>
          )}
        </section>
      )}

      {/* Cards / Lista — #78a: dim sutil durante refetch (não limpa a lista) */}
      {ncs.length === 0 ? (
        semFiltrosAtivos ? (
          /* #238: conta vazia → não abre em tela branca; oferece a NC de exemplo. */
          <StateBlock
            kind="empty"
            icon="science"
            title="Comece explorando um exemplo"
            message="Você ainda não tem não conformidades. Carregue uma NC de exemplo — já analisada (Ishikawa 6M, 5 porquês, causa raiz, plano de ação) — pra ver como o sistema funciona antes de registrar a sua."
            action={
              <Button icon="auto_awesome" onClick={handleCarregarExemplo} disabled={carregandoExemplo}>
                {carregandoExemplo ? 'Carregando…' : 'Ver uma NC de exemplo'}
              </Button>
            }
          />
        ) : (
          <StateBlock
            kind="empty"
            title="Nenhuma NC encontrada"
            message="Nenhuma NC corresponde aos filtros aplicados."
          />
        )
      ) : viewMode === 'list' ? (
        <section className="nc-list-table">
          <div className="nc-list-row nc-list-header-row">
            {isAdmin && <span className="nc-list-cell nc-list-cell-check"></span>}
            <span className="nc-list-cell nc-list-cell-id">ID</span>
            <span className="nc-list-cell nc-list-cell-produto">Produto / Lote</span>
            <span className="nc-list-cell nc-list-cell-status">Status</span>
            <span className="nc-list-cell nc-list-cell-sev">Severidade</span>
            <span className="nc-list-cell nc-list-cell-score">Score</span>
            <span className="nc-list-cell nc-list-cell-date">Data</span>
          </div>
          {ncs.map(nc => (
            <div
              key={nc.id}
              className={`nc-list-row${selectedIds.has(nc.id) ? ' selected' : ''}`}
              onClick={() => onSelectNC(nc.id)}
            >
              {isAdmin && (
                <span className="nc-list-cell nc-list-cell-check">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(nc.id)}
                    onChange={(e) => toggleSelect(e, nc.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </span>
              )}
              <span className="nc-list-cell nc-list-cell-id">{nc.numero_nc || `NC-${nc.id}`}</span>
              <span className="nc-list-cell nc-list-cell-produto">
                <strong>{nc.produto || 'N/A'}</strong>
                {!isServiceSector(nc.industria) && <small>Lote: {nc.lote || 'N/A'}</small>}
                <span className="nc-badges-row">
                  {getOrigemBadge(nc)}
                  {getEficaciaBadge(nc)}
                </span>
              </span>
              <span className="nc-list-cell nc-list-cell-status">{getStatusBadge(nc.workflow_status)}</span>
              <span className="nc-list-cell nc-list-cell-sev">{getSeverityBadge(nc.severidade)}</span>
              <span className="nc-list-cell nc-list-cell-score">
                {nc.modo_analise !== 'registro' && nc.score_qualidade != null ? `${nc.score_qualidade}/100` : '—'}
              </span>
              <span className="nc-list-cell nc-list-cell-date">{formatDate(nc.created_at)}</span>
            </div>
          ))}
        </section>
      ) : (
        <section className="nc-grid">
          {ncs.map(nc => (
            <div
              key={nc.id}
              className={`nc-card${selectedIds.has(nc.id) ? ' selected' : ''}`}
              onClick={() => onSelectNC(nc.id)}
            >
              {/* Checkbox de seleção (admin) */}
              {isAdmin && (
                <input
                  type="checkbox"
                  className="nc-card-checkbox"
                  checked={selectedIds.has(nc.id)}
                  onChange={(e) => toggleSelect(e, nc.id)}
                  onClick={(e) => e.stopPropagation()}
                />
              )}

              {/* Floating ID badge */}
              <span className="nc-card-id-badge">{nc.numero_nc || `NC-${nc.id}`}</span>

              {/* Card header */}
              <div className="nc-card-header">
                <div className="nc-card-title-group">
                  <h3 className="nc-card-title">{nc.produto || 'N/A'}</h3>
                  {!isServiceSector(nc.industria) && (
                    <p className="nc-card-lote">
                      Lote: <span>{nc.lote || 'N/A'}</span>
                    </p>
                  )}
                  <span className="nc-badges-row">
                    {getOrigemBadge(nc)}
                    {getEficaciaBadge(nc)}
                  </span>
                </div>
                {getStatusBadge(nc.workflow_status)}
              </div>

              {/* Description */}
              <p className="nc-card-description">
                {nc.descricao || 'Sem descrição.'}
              </p>

              {/* Footer: severity + score */}
              <div className="nc-card-footer">
                {getSeverityBadge(nc.severidade)}
                {nc.modo_analise !== 'registro' && nc.score_qualidade != null && (
                  <div className="nc-score-group">
                    <span className="nc-score-label">Completude</span>
                    <span className="nc-score-value">{nc.score_qualidade}/100</span>
                  </div>
                )}
              </div>

              {/* Meta row: date + link */}
              <div className="nc-card-meta">
                <span className="nc-card-date">{formatDate(nc.created_at)}</span>
                <span className="nc-card-link">
                  Ver Detalhes →
                </span>
              </div>
            </div>
          ))}
        </section>
      )}

      {/* #78a: paginação real server-side (corrige bug #98 dos botões fixos) */}
      {total > 0 && (
        <footer className="nc-pagination">
          <p className="nc-pagination-info">
            Mostrando {(currentPage - 1) * PER_PAGE + 1}–{Math.min(currentPage * PER_PAGE, total)} de {total} não conformidades
          </p>
          {totalPages > 1 && (
            <div className="nc-pagination-buttons">
              <button
                className="nc-pagination-btn nav"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1 || loading}
                aria-label="Página anterior"
              >‹</button>
              {paginas().map((p, i) =>
                p === '…' ? (
                  <span key={`gap-${i}`} className="nc-pagination-gap">…</span>
                ) : (
                  <button
                    key={p}
                    className={`nc-pagination-btn ${p === currentPage ? 'active' : ''}`}
                    onClick={() => setCurrentPage(p)}
                    disabled={loading}
                  >{p}</button>
                )
              )}
              <button
                className="nc-pagination-btn nav"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages || loading}
                aria-label="Próxima página"
              >›</button>
            </div>
          )}
        </footer>
      )}
    </div>
  );
}

export default NCList;
